import { useState, useEffect, useId, useRef } from 'react';
import { Link, useNavigate } from 'react-router';
import { ChevronRight, ChevronUp } from 'lucide-react';
import { useTranslation } from '@/i18n';
import { useAuthStore } from '@/store/authStore';
import { LanguageCode, SUPPORTED_LANGUAGES } from '@/types/i18n';
import { useLocalization } from '@/i18n';
import { useTheme } from '@/theme';
import { logLogout } from '@/utils/authLogger';
import { logAdminConsoleAccess } from '@/utils/adminLogger';
import LangFlag from './LangFlag';
import { useCreateEntityMenuItems } from './CreateMenuItems';
import './UserDropdown.css';

interface UserDropdownProps {
  variant?: 'avatar' | 'sidebar' | 'rail';
}

export default function UserDropdown({ variant = 'avatar' }: UserDropdownProps) {
  const t = useTranslation();
  const navigate = useNavigate();
  const user = useAuthStore(state => state.user);
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);
  const logout = useAuthStore(state => state.logout);
  const {
    language,
    pendingLanguage,
    setLanguage,
    reloadTranslations,
    resetLocaleCache,
    copyLocaleDiagnostics,
    isLoading: isLocaleLoading,
    error: localeError,
  } = useLocalization();
  const { theme, setTheme } = useTheme();

  const [isOpen, setIsOpen] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [diagnosticsCopyState, setDiagnosticsCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownPanelId = useId();
  const createItems = useCreateEntityMenuItems(() => { setIsCreateOpen(false); setIsOpen(false); });

  // Закрытие dropdown при клике вне его
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setIsCreateOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleLogout = () => {
    // Логируем выход пользователя
    if (user?.email) {
      logLogout(user.email);
    }
    logout();
    setIsOpen(false);
    navigate('/');
  };

  const handleAdminPanelClick = () => {
    // Логируем вход в админ-панель
    if (user?.email) {
      logAdminConsoleAccess(user.email);
    }
    setIsOpen(false);
  };

  const handleLanguageChange = (langCode: LanguageCode) => {
    setDiagnosticsCopyState('idle');
    setLanguage(langCode);
  };

  const handleCopyLocaleDiagnostics = async () => {
    setDiagnosticsCopyState(await copyLocaleDiagnostics() ? 'copied' : 'failed');
  };

  const handleThemeChange = (newTheme: 'light' | 'dark' | 'auto') => {
    setTheme(newTheme);
  };

  const getThemeIcon = (themeValue: string) => {
    if (themeValue === 'auto') return '🌓';
    return themeValue === 'dark' ? '🌙' : '☀️';
  };

  const getThemeLabel = (themeValue: string) => {
    return t(`theme.${themeValue}`, {}, themeValue.charAt(0).toUpperCase() + themeValue.slice(1));
  };

  const isAdmin = user?.is_admin ?? false;
  const isLocalizator = user?.is_localizator ?? false;
  const isSidebar = variant === 'sidebar';
  const accountLabel = isAuthenticated && user?.email
    ? user.email
    : t('auth.login.submit', {}, 'Sign In');

  return (
    <div className={`user-dropdown user-dropdown--${variant}`} ref={dropdownRef}>
      <button
        className={isSidebar ? 'user-account-trigger' : 'user-avatar'}
        onClick={() => setIsOpen(!isOpen)}
        aria-label={t('language.userMenu', {}, 'User menu')}
        aria-expanded={isOpen}
        aria-controls={dropdownPanelId}
      >
        {isSidebar ? (
          <>
            <span className="user-account-copy">
              <span className="user-account-kicker">{t('language.userMenu', {}, 'User menu')}</span>
              <span className="user-account-name">{accountLabel}</span>
            </span>
            <ChevronUp className="user-account-chevron" size={16} aria-hidden="true" />
          </>
        ) : (
          <div className="avatar-placeholder">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="8" r="4" fill={isAuthenticated && user ? 'var(--m13-color-6b7280)' : 'var(--m13-color-9ca3af)'} />
              <path d="M12 14C8 14 4 16 4 18V20H20V18C20 16 16 14 12 14Z" fill={isAuthenticated && user ? 'var(--m13-color-6b7280)' : 'var(--m13-color-9ca3af)'} />
            </svg>
          </div>
        )}
      </button>

      {isOpen && (
        <div
          id={dropdownPanelId}
          className="user-dropdown-menu"
          role="region"
          aria-label={t('language.userMenu', {}, 'User menu')}
        >
          {/* Auth section */}
          <div className="dropdown-section">
            {isAuthenticated ? (
              <div className="user-info">
                <div className="user-email">{user?.email}</div>
                <Link
                  to="/user"
                  className="dropdown-item user-panel"
                  onClick={() => setIsOpen(false)}
                >
                  👤 {t('navigation.userPanel', {}, 'User Panel')}
                </Link>
                {isLocalizator && (
                  <Link
                    to="/localization"
                    className="dropdown-item localization-panel"
                    onClick={() => setIsOpen(false)}
                  >
                    🌍 {t('navigation.localizationPanel', {}, "Localization Panel")}
                  </Link>
                )}
                {isAdmin && (
                  <Link
                    to="/admin"
                    className="dropdown-item admin-panel"
                    onClick={handleAdminPanelClick}
                  >
                    ⚙️ {t('navigation.adminPanel', {}, 'Admin Panel')}
                  </Link>
                )}
                <button onClick={handleLogout} className="dropdown-item logout">
                  🔓 {t('auth.logout', {}, 'Logout')}
                </button>
              </div>
            ) : (
              <Link to="/login" className="dropdown-item login" onClick={() => setIsOpen(false)}>
                🔑 {t('auth.login.submit', {}, 'Sign In')}
              </Link>
            )}
          </div>

          <div className="dropdown-separator" />

          {/* Legal section */}
          <div className="dropdown-section">
            <div className="dropdown-label">{t('legal.navigation', {}, 'Legal documents')}</div>
            <div className="dropdown-items">
              <Link to="/legal/privacy" className="dropdown-item legal-link" onClick={() => setIsOpen(false)}>
                {t('legal.navigation', {}, 'Legal documents')}
              </Link>
            </div>
          </div>

          <div className="dropdown-separator" />

          {/* Language section */}
          <div className="dropdown-section">
            <div className="dropdown-label">{t('navigation.language', {}, 'Language')}</div>
            <div className="language-icons">
              {SUPPORTED_LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  className={[
                    'language-icon-btn',
                    language === lang.code ? 'active' : '',
                    pendingLanguage === lang.code ? 'pending' : '',
                  ].filter(Boolean).join(' ')}
                  onClick={() => handleLanguageChange(lang.code)}
                  title={lang.name}
                  aria-label={lang.name}
                  aria-pressed={language === lang.code}
                  aria-busy={isLocaleLoading && pendingLanguage === lang.code}
                >
                  <LangFlag flag={lang.flag} />
                </button>
              ))}
            </div>
            {pendingLanguage && isLocaleLoading && (
              <div className="language-load-status" role="status">
                {t(
                  'language.loading',
                  { language: SUPPORTED_LANGUAGES.find(item => item.code === pendingLanguage)?.name ?? pendingLanguage },
                  "Loading {{language}}…",
                )}
              </div>
            )}
            {pendingLanguage && localeError && !isLocaleLoading && (
              <div className="language-load-error" role="alert">
                <span>{t('language.loadError', {}, "The selected language could not be loaded. The previous language remains active.")}</span>
                <div className="language-recovery-actions">
                  <button type="button" onClick={() => void reloadTranslations()}>
                    {t('language.retry', {}, 'Retry')}
                  </button>
                  <button type="button" onClick={() => void resetLocaleCache()}>
                    {t('language.resetCache', {}, 'Reset locale cache')}
                  </button>
                </div>
              </div>
            )}
            <button
              type="button"
              className="language-diagnostics-button"
              onClick={() => void handleCopyLocaleDiagnostics()}
            >
              {t('language.copyDiagnostics', {}, 'Copy localization diagnostics')}
            </button>
            {diagnosticsCopyState !== 'idle' && (
              <div
                className={diagnosticsCopyState === 'copied' ? 'language-copy-status' : 'language-copy-error'}
                role="status"
              >
                {diagnosticsCopyState === 'copied'
                  ? t('language.diagnosticsCopied', {}, 'Localization diagnostics copied.')
                  : t('language.diagnosticsCopyFailed', {}, 'Could not copy diagnostics.')}
              </div>
            )}
          </div>

          <div className="dropdown-separator" />

          {/* Theme section */}
          <div className="dropdown-section">
            <div className="dropdown-label">{t('theme.select', {}, "Select theme")}</div>
            <div className="theme-icons">
              {(['light', 'dark', 'auto'] as const).map((themeOption) => (
                <button
                  key={themeOption}
                  className={`theme-icon-btn ${theme === themeOption ? 'active' : ''}`}
                  onClick={() => handleThemeChange(themeOption)}
                  title={getThemeLabel(themeOption)}
                >
                  {getThemeIcon(themeOption)}
                </button>
              ))}
            </div>
          </div>

          <div className="dropdown-separator" />

          {/* Create section shares the same actions and handlers as the primary create menu. */}
          <div className="dropdown-section">
            <button
              type="button"
              className="dropdown-item user-create-trigger"
              aria-expanded={isCreateOpen}
              onClick={() => setIsCreateOpen(value => !value)}
            >
              <span>{t('common.create')}</span><ChevronRight size={15} aria-hidden="true" />
            </button>
            {isCreateOpen && (
              <div className="user-create-submenu" role="menu" aria-label={t('common.create')}>
                {createItems.map(item => (
                  <button key={String(item.label)} type="button" className="dropdown-item user-create-item" role="menuitem" onClick={item.onSelect}>
                    {item.icon}<span><strong>{item.label}</strong>{item.description ? <small>{item.description}</small> : null}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
